import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { forgetThisDevice, registerSessionApplier, TwoFactorRequiredError } from '@saas/module-core/ui';
import { AuthContextProvider, makeCan, setDisplayTimeZone, type AuthContextValue, type AuthMe, type LoginInput, type SignupInput } from '@saas/ui';
import { tenantTimeZone } from '@saas/shared';
import { api } from './client.js';
import { tenantStore, tokenStore } from './tokens.js';

interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresInSec: number;
}

/** 段 5: 2 要素認証が有効な利用者への応答（トークンは入っていない） */
interface TwoFactorChallenge {
  twoFactorRequired: true;
  twoFactorToken: string;
  expiresInSec: number;
}

/**
 * AuthProvider: 起動時はリフレッシュトークンで復元 → GET /auth/me。
 * login / signup は POST /auth/login|signup の応答（tokens + me）をそのまま使う。
 * can() は @saas/shared の resolveScope / hasPermission（サーバと同じ規則）。
 */
export function AuthProvider({ children, onLoggedOut }: { children: ReactNode; onLoggedOut: () => void }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');
  const [me, setMe] = useState<AuthMe | null>(null);
  const loggedOutRef = useRef(onLoggedOut);
  loggedOutRef.current = onLoggedOut;

  // 表示のタイムゾーンはテナントの設定（02 §11 #4「保存 UTC・表示はテナント TZ」）。me が変わるたびに合わせる。ログアウト後は既定に戻す
  useEffect(() => {
    setDisplayTimeZone(me ? tenantTimeZone(me.tenant.settings) : null);
  }, [me]);

  const becomeAnonymous = useCallback(() => {
    tokenStore.clear();
    setMe(null);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  // 401 で復旧できなかったとき（client.ts から）
  useEffect(() => {
    api.setUnauthenticatedHandler(() => {
      becomeAnonymous();
      loggedOutRef.current();
    });
  }, [becomeAnonymous]);

  // 起動時の復元
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!tokenStore.getRefresh()) {
        if (alive) setStatus('anonymous');
        return;
      }
      const ok = await api.refreshTokens();
      if (!ok) {
        if (alive) becomeAnonymous();
        return;
      }
      try {
        const m = await api.get<AuthMe>('/auth/me');
        if (!alive) return;
        setMe(m);
        setStatus('authenticated');
      } catch {
        if (alive) becomeAnonymous();
      }
    })();
    return () => {
      alive = false;
    };
  }, [becomeAnonymous]);

  const applySession = useCallback((tokens: TokenPair, m: AuthMe) => {
    tokenStore.setAccess(tokens.accessToken);
    tokenStore.setRefresh(tokens.refreshToken);
    tenantStore.remember(m.tenant.slug);
    setMe(m);
    setStatus('authenticated');
  }, []);

  const login = useCallback(
    async (input: LoginInput) => {
      if (input.tenantSlug) tenantStore.remember(input.tenantSlug.trim().toLowerCase());
      // サブドメインで会社が決まらない環境では body でも slug を渡す（本番 API は X-Tenant ヘッダを見ない）
      const tenantSlug = input.tenantSlug?.trim().toLowerCase() || undefined;
      const r = await api.post<TokenPair & { me: AuthMe } & Partial<TwoFactorChallenge>>(
        '/auth/login',
        { email: input.email, password: input.password, ...(tenantSlug ? { tenantSlug } : {}) },
        { skipAuthRefresh: true },
      );
      // 段 5: 2 要素認証が有効な利用者にはトークンが出ない。画面が 2 段目（POST /auth/2fa/verify）へ進む
      if (r.twoFactorRequired && r.twoFactorToken) throw new TwoFactorRequiredError(r.twoFactorToken, r.expiresInSec);
      applySession(r, r.me);
    },
    [applySession],
  );

  // 段 5: 2 段目（2 要素認証）と Google / Microsoft ログインの戻り先が、ここへセッションを渡してくる
  useEffect(() => {
    registerSessionApplier((s) => applySession({ accessToken: s.accessToken, refreshToken: s.refreshToken, expiresInSec: s.expiresInSec }, s.me));
    return () => registerSessionApplier(null);
  }, [applySession]);

  const signup = useCallback(
    async (input: SignupInput) => {
      tenantStore.remember(input.slug);
      const r = await api.post<TokenPair & { me: AuthMe }>('/auth/signup', input, { skipAuthRefresh: true });
      applySession(r, r.me);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    // この端末の Web Push 購読を解く（共有 PC でログアウト後も通知が届き続けないように）。届かなくても続ける
    try {
      await forgetThisDevice(api);
    } catch {
      /* ignore */
    }
    const refreshToken = tokenStore.getRefresh();
    if (refreshToken) {
      try {
        await api.post('/auth/logout', { refreshToken }, { skipAuthRefresh: true });
      } catch {
        /* サーバに届かなくても端末側は消す */
      }
    }
    becomeAnonymous();
    loggedOutRef.current();
  }, [becomeAnonymous]);

  const refreshMe = useCallback(async () => {
    const m = await api.get<AuthMe>('/auth/me');
    setMe(m);
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const can = makeCan(me?.permissions);
    const roles = me?.roles ?? [];
    return {
      status,
      me,
      can,
      canAny: (...ps) => ps.some((p) => can(p)),
      isAdmin: roles.includes('owner') || roles.includes('admin'),
      isModuleEnabled: (name) => name === 'core' || (me?.enabledModules ?? []).includes(name),
      login,
      signup,
      logout,
      refreshMe,
      needsTenantSlug: tenantStore.needsHeader(),
      storedTenantSlug: tenantStore.stored(),
    };
  }, [status, me, login, signup, logout, refreshMe]);

  return <AuthContextProvider value={value}>{children}</AuthContextProvider>;
}
